// Import university logos
import { cn } from '@listening/utils/react-helpers';
import harvardUniversityLogo1Png from '@listening/assets/astro/images/Harvard_University_logo-1.png';
import mitSvg from '@listening/assets/astro/images/mit.svg';
import Cornell from '@listening/assets/astro/images/new-design-25/homepage/uni-logo-cornell.svg';
import NewYork from '@listening/assets/astro/images/new-design-25/homepage/uni-logo-newYork.svg';
import Princeton from '@listening/assets/astro/images/new-design-25/homepage/uni-logo-princeton.svg';
import Stanford from '@listening/assets/astro/images/new-design-25/homepage/uni-logo-stanford.svg';
import rectangle458Svg from '@listening/assets/astro/images/Rectangle-458.svg';
import Columbia from '@listening/assets/astro/images/UniBanner-Columbia.svg';
import Hartford from '@listening/assets/astro/images/UniBanner-Hartford.svg';
import yaleSvg from '@listening/assets/astro/images/yale.svg';

import * as m from '@/paraglide/messages';
import { useEffect, useRef, useState } from 'react';

type University = {
  src: string;
  alt: string;
  name: string;
};

const universities: Array<University> = [
  {
    src: harvardUniversityLogo1Png,
    alt: 'Harvard University',
    name: 'Harvard University',
  },
  { src: mitSvg, alt: 'Massachusetts Institute of Technology', name: 'MIT' },
  { src: yaleSvg, alt: 'Yale University', name: 'Yale University' },
  {
    src: rectangle458Svg,
    alt: 'Columbia University',
    name: 'Columbia University',
  },
  { src: Columbia, alt: 'Columbia University', name: 'Columbia University' },
  { src: Hartford, alt: 'University of Hartford', name: 'Hartford University' },
  { src: Princeton, alt: 'Princeton University', name: 'Princeton University' },
  { src: Stanford, alt: 'Stanford University', name: 'Stanford University' },
  { src: Cornell, alt: 'Cornell University', name: 'Cornell University' },
  { src: NewYork, alt: 'New York University', name: 'New York University' },
];

const UniversityBanner = ({
  direction = 'left',
  speed = 'normal',
  pauseOnHover = true,
}: {
  direction?: 'left' | 'right';
  speed?: 'fast' | 'normal' | 'slow';
  pauseOnHover?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    if (containerRef.current && scrollerRef.current) {
      const content = Array.from(scrollerRef.current.children);
      for (const child of content) {
        const clone = child.cloneNode(true);
        scrollerRef.current.append(clone);
      }

      // Set direction
      containerRef.current.style.setProperty(
        '--animation-direction',
        direction === 'left' ? 'forwards' : 'reverse',
      );

      // Set speed
      const duration =
        speed === 'fast' ? '20s' : speed === 'normal' ? '40s' : '80s';
      containerRef.current.style.setProperty('--animation-duration', duration);

      setStart(true);
    }
  }, [direction, speed]);

  return (
    <div
      aria-label={m.homepage_university_aria()}
      className={cn(
        'relative w-full overflow-hidden py-8',
        'mask-[linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]',
      )}
      ref={containerRef}
      role='region'
    >
      <div
        className={cn(
          'flex w-max min-w-full shrink-0 flex-nowrap gap-8 px-4 md:gap-12 lg:gap-16',
          start && 'animate-scroll',
          pauseOnHover && 'hover:paused',
        )}
        ref={scrollerRef}
      >
        {universities.map((uni, index) => (
          <div
            className='flex shrink-0 items-center justify-center'
            key={`${uni.name}-${String(index)}`}
          >
            <img
              alt={uni.alt}
              className='h-auto w-[60px] object-contain opacity-60 transition-opacity hover:opacity-100 sm:w-[80px] md:w-[100px] lg:w-[120px]'
              decoding='async'
              height={60}
              loading='lazy'
              src={uni.src}
              width={120}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default UniversityBanner;
